import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import PagesTable from "../../components/admin/PagesTable";
import { errorMessage, pagesApi } from "../../lib/api";
import { cn } from "../../lib/utils";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "published", label: "Published" },
  { id: "draft", label: "Drafts" },
];

const CRUMBS = [{ label: "Landing Pages" }];

const PagesList = () => {
  const [pages, setPages] = useState(null);
  const [status, setStatus] = useState("all");
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    try {
      setPages(await pagesApi.list());
    } catch (err) {
      toast.error(errorMessage(err, "Could not load pages"));
      setPages([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    if (!pages) return null;
    const q = query.trim().toLowerCase();
    return pages.filter((p) => (status === "all" || p.status === status) && (!q || p.title.toLowerCase().includes(q) || p.slug.includes(q)));
  }, [pages, status, query]);

  const counts = useMemo(() => {
    const all = pages || [];
    return { all: all.length, published: all.filter((p) => p.status === "published").length, draft: all.filter((p) => p.status === "draft").length };
  }, [pages]);

  return (
    <div className="space-y-6" data-testid="pages-list">
      <AdminPageHeader
        crumbs={CRUMBS}
        eyebrow="Promotions"
        title="Landing Pages"
        description="Edit, publish, duplicate, copy campaign links and open stats for every promo page."
        actions={
          <Button asChild className="h-11 rounded-none px-6 text-[11px] font-semibold uppercase tracking-[0.2em]" data-testid="pages-new-page">
            <Link to="/admin/pages/new">
              <Plus className="mr-2 h-4 w-4" /> New landing page
            </Link>
          </Button>
        }
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-2" data-testid="pages-status-filters">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setStatus(f.id)}
              className={cn(
                "flex items-center gap-2 border px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] transition-colors",
                status === f.id ? "border-primary bg-primary text-primary-foreground" : "border-border text-foreground/80 hover:border-foreground/50"
              )}
              data-testid={`pages-filter-${f.id}`}
            >
              {f.label} <span className={cn("text-[10px]", status === f.id ? "text-primary-foreground/80" : "text-muted-foreground")}>{counts[f.id]}</span>
            </button>
          ))}
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by title or slug" className="h-10 rounded-none border-border bg-transparent pl-9 pr-9 text-sm" data-testid="pages-search" />
          {query && (
            <button type="button" onClick={() => setQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground" aria-label="Clear search">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <PagesTable pages={filtered} onChanged={load} emptyTitle={pages && pages.length ? "No pages match" : "No landing pages yet"} />
    </div>
  );
};

export default PagesList;
